/**
 * @file LookColumnsGroup.tsx
 * @description Группа «Колонки» секции (phase19.16 Р2; uiux-design-r2 §2).
 * SB120 этап 1 «секция-герой» (uiux-sb120-stage1 §2.5, «Дополнение» §2.8;
 * данные db-design-sb120-stage1 §1):
 *
 * ```
 * Колонок  [1][2][3][4]
 * Доли (из 12) — полоса с границами, −/+, быстрые схемы
 * Между колонками / Между элементами   0–200 px  [S][M][L]  ↺ как у сайта (M)
 * По вертикали  [Сверху][По центру][Снизу][Растянуть]
 * ▸ Колонка N — фон, положение, внутренние отступы
 * ```
 *
 * Колонок стало меньше — элементы переносятся в последнюю оставшуюся,
 * сначала спрашиваем (`confirm`). Узкая колонка — предупреждение
 * (`sectionLookWarnings`), не запрет.
 */

import { DEFAULT_SITE_THEME, equalShares, SITE_LIMITS, spaceStepPx, type SectionColumns, type SectionPayload } from "@chaiberi/shared";

import { Label } from "../../../ui/label";
import { FieldHint, SettingsSubgroup } from "../../../ui/settings-groups";
import type { ConfirmRequest } from "../../../ui/use-confirm";
import { Segmented } from "../../../site-view/Segmented";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { LookWarningNote } from "../fields/LookWarningNote";
import { useSectionForm } from "../section-form-context";
import { ColumnLookFields } from "./ColumnLookFields";
import { ColumnSharesField } from "./ColumnSharesField";
import { spacePresets } from "../../../../lib/site-builder/look-space";
import { VALIGN_LABELS } from "../../../../lib/site-builder/look-summary";
import { setColumns } from "../../../../lib/site-builder/section-edit";

const COUNTS = (["1", "2", "3", "4"] as const).map((value) => ({ value, label: value }));
const VALIGN = (["top", "center", "bottom", "stretch"] as const).map((value) => ({ value, label: VALIGN_LABELS[value] }));

interface Props {
  payload: SectionPayload;
  onChange: (payload: SectionPayload) => void;
  confirm: (request: ConfirmRequest) => Promise<boolean>;
}

export function LookColumnsGroup({ payload, onChange, confirm }: Props) {
  const { preview, theme } = useSectionForm();
  const density = (theme ?? DEFAULT_SITE_THEME).spacing.density;
  const columns = payload.columns;
  const count = columns.items.length;
  const set = (patch: Partial<SectionColumns>) => onChange({ ...payload, columns: { ...columns, ...patch } });

  async function onShares(shares: number[]) {
    const next = setColumns(payload, shares);
    if (next.moved > 0) {
      const items = next.payload.columns.items;
      const inLast = items[items.length - 1]?.elements.length ?? 0;
      const over = inLast > SITE_LIMITS.elementsPerColumn
        ? ` В ней окажется ${inLast} элементов, а можно не больше ${SITE_LIMITS.elementsPerColumn} — лишние придётся перенести в новый блок, иначе блок не сохранится.`
        : "";
      const ok = await confirm({
        title: "Убрать колонки?",
        description: `Элементы из убранных колонок (${next.moved}) перейдут в последнюю оставшуюся колонку.${over}`,
        action: "Убрать",
      });
      if (!ok) return;
    }
    onChange(next.payload);
  }
  const gapField = (key: "gap" | "elementGap", label: string) => (
    <NumberSliderField
      label={label}
      value={columns[key]}
      inherit={{ value: spaceStepPx("m", density), label: "как у сайта" }}
      min={0}
      max={200}
      step={1}
      unit="px"
      display={{ factor: 1, decimals: 2 }}
      presets={spacePresets(density, ["s", "m", "l"])}
      onChange={(v) => {
        const next = { ...columns };
        if (v === undefined) delete next[key];
        else next[key] = v;
        onChange({ ...payload, columns: next });
      }}
      onPreview={(v) => preview(`columns.${key}`, v)}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Label>Колонок</Label>
        <Segmented aria-label="Сколько колонок" options={COUNTS} value={String(count) as (typeof COUNTS)[number]["value"]} onChange={(n) => {
            // То же число колонок — доли не трогаем (иначе 7+5 сбросились бы в 6+6).
            if (Number(n) === count) return;
            void onShares(equalShares(Number(n)));
          }} />
      </div>
      {count > 1 ? <ColumnSharesField shares={columns.shares} onChange={(shares) => void onShares(shares)} /> : null}
      <LookWarningNote paths={columns.shares.map((_, i) => `columns.shares.${i}`)} />
      {count > 1 ? gapField("gap", "Между колонками") : null}
      {gapField("elementGap", "Между элементами")}
      <div className="flex flex-col gap-1">
        <Label>По вертикали</Label>
        <Segmented aria-label="Колонки в ряду по вертикали" options={VALIGN} value={columns.valign} onChange={(valign) => set({ valign })} />
        {payload.look.place?.y === "stretch" || payload.look.placePhone?.y === "stretch" ? (
          <FieldHint className="mt-0">У блока включено «Растянуть»: колонки без своего положения растянуты на всю высоту, это значение для них не действует.</FieldHint>
        ) : null}
      </div>
      {columns.items.map((column, i) => (
        <SettingsSubgroup key={i} title={`Колонка ${i + 1}${count > 1 ? ` · ${columns.shares[i] ?? 12}/12` : ""}${column.bg ? "" : " · без фона"}`}>
          <ColumnLookFields
            column={column}
            index={i}
            valign={columns.valign}
            onChange={(next) => set({ items: columns.items.map((c, j) => (j === i ? next : c)) })}
          />
        </SettingsSubgroup>
      ))}
    </div>
  );
}
